import type { Profiles } from './people';
import { displayName } from './people';

/**
 * Mentions are stored as the plain address — `@someone@example.com`.
 *
 * Storing the address rather than the name means a comment still points at the
 * right person after they change how their name reads, and it needs no lookup
 * table. The interface shows the name; the text underneath holds the identity.
 */
const MENTION = /@([\w.+-]+@[\w.-]+\.\w+)/g;

function mentionedIn(text: string): string[] {
  return [...new Set(Array.from(text.matchAll(MENTION), (m) => m[1].toLowerCase()))];
}

export function mentions(text: string, email: string): boolean {
  return !!email && mentionedIn(text).includes(email.toLowerCase());
}

/** Splits a comment into plain runs and mention runs, for rendering. */
export function splitMentions(
  text: string,
  profiles: Profiles
): { text: string; mention?: string }[] {
  const out: { text: string; mention?: string }[] = [];
  let last = 0;
  for (const m of text.matchAll(MENTION)) {
    if (m.index! > last) out.push({ text: text.slice(last, m.index) });
    const addr = m[1].toLowerCase();
    out.push({ text: `@${displayName(addr, profiles)}`, mention: addr });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/**
 * STORED TEXT, SHOWN BY NAME — the inverse of `resolveMentions`.
 *
 * A stored comment holds `@pat@example.com`; the read view shows `@Pat Moore`
 * through `splitMentions`. Seeding an in-place editor with the raw stored text
 * therefore showed the address while everything else showed the name. This
 * renders the same `@mention`s as names — reusing `splitMentions`, so there is
 * one parser — and leaves everything else exactly as typed, INCLUDING a bare
 * un-mentioned address (which is not a mention and must not be rewritten). So
 * `resolveMentions` turns the result straight back into byte-identical storage.
 */
export function nameMentions(text: string, profiles: Profiles): string {
  return splitMentions(text, profiles)
    .map((part) => part.text)
    .join('');
}

/**
 * What has been typed after the last `@`, or null if nobody is being mentioned.
 *
 * Returned separately from the matching so the picker, the filtering and the
 * completion all agree on where the mention starts and ends. They were three
 * copies of the same regular expression, and a change to one was a change to
 * two thirds of the behaviour.
 */
/**
 * NAME THE PEOPLE IN A LINE THE SERVER WROTE.
 *
 * Some trail entries are plain text with a raw address baked in — "set assignee
 * to pat@example.com" — written by a database trigger that has no profile table
 * to reach. Everywhere else a person is shown by name, so this swaps any bare
 * address for the name, and leaves an `@mention` alone for `splitMentions` to
 * render with its own styling. An unknown address falls back through
 * `displayName` to the part before the @, never worse than the raw address.
 *
 * The character before the address is captured and put back rather than matched
 * with a look-behind: it keeps the `@` of a mention out (an `@` is not a
 * boundary character), and it is the address form the whole app can run, no
 * look-behind being used anywhere else here.
 */
const BARE_ADDRESS = /(^|[^\w.@+-])([\w][\w.+-]*@[\w.-]+\.\w+)/g;

export function namePeople(text: string, profiles: Profiles): string {
  return text.replace(BARE_ADDRESS, (_m, before, addr) => before + displayName(addr.toLowerCase(), profiles));
}

export function mentionFragment(text: string, caret: number = text.length): string | null {
  // What is being typed is whatever `@word` ends AT THE CARET, not at the end of
  // the whole field — otherwise putting the caret back into a sentence and typing
  // "@" saw only the text's final word and never opened the picker. The fragment
  // is read from the slice up to the caret; the caret defaults to the end, so a
  // plain `mentionFragment(text)` behaves exactly as before.
  const m = text.slice(0, caret).match(/@([\w.+-]*)$/);
  return m ? m[1] : null;
}

/**
 * Who matches what has been typed so far, best first.
 *
 * Matching on the start of any word in the name is what people expect: "mo"
 * finds Pat Moore. The address is matched too, because somebody who knows the
 * address should not have to remember how the name is spelled.
 *
 * Name matches come before address-only ones, so the obvious answer is the one
 * Tab completes.
 */
export function matchPeople(fragment: string, emails: string[], profiles: Profiles): string[] {
  const f = fragment.trim().toLowerCase();
  if (!f) return emails;
  const startsAWord = (email: string) =>
    displayName(email, profiles)
      .toLowerCase()
      .split(/\s+/)
      .some((word) => word.startsWith(f));
  const byName = emails.filter(startsAWord);
  const byAddress = emails.filter((e) => !startsAWord(e) && e.toLowerCase().startsWith(f));
  return [...byName, ...byAddress];
}

/**
 * Replace the half-typed mention with a real one, ready to keep typing after.
 *
 * What goes into the box is the NAME — `@Pat Moore` — so a person writing a
 * comment reads the same thing they will read once it is posted, never a raw
 * address. The address is put back by `resolveMentions` at the moment it is
 * saved, so the identity that survives a rename and drives the notification is
 * still what is stored; only what is shown while typing changes.
 */
export function applyMention(
  text: string,
  email: string,
  profiles: Profiles,
  caret: number = text.length
): string {
  // Replace the half-typed `@word` that ends at the caret — leaving everything
  // after the caret untouched — and drop a space after the name to carry on. With
  // the caret at the end this is the old behaviour exactly; mid-sentence it
  // completes the mention in place instead of at the end of the line. The space is
  // skipped when the text already continues with whitespace, so completing before
  // an existing space does not leave two. The new caret belongs right after the
  // inserted name, which the field works out as `result.length - after.length`.
  const after = text.slice(caret);
  const sep = /^\s/.test(after) ? '' : ' ';
  const before = text.slice(0, caret).replace(/@[\w.+-]*$/, `@${displayName(email, profiles)}${sep}`);
  return before + after;
}

/**
 * Turn the names shown while composing back into the addresses that are stored.
 *
 * The composer shows `@Pat Moore`; the stored comment must hold `@pat@…` — that
 * address is what makes a mention survive a rename and what the fan-out reads to
 * decide who to tell. So on the way to saving, every known person's name is
 * swapped back for their address. Longest names first, so "Pat Moore (finance)"
 * is matched before "Pat Moore" and never left half-converted; the match is a
 * plain string, never a pattern, so a name with punctuation is safe.
 *
 * A `@name` that matches nobody is left as plain text — exactly what it is — so
 * a mention can never be quietly invented, and one whose name was edited until
 * it no longer matches simply stops being a mention, which the "Mentioning…"
 * line under the box reflects because it reads this same resolved text.
 */
export function resolveMentions(text: string, emails: string[], profiles: Profiles): string {
  const byLongestName = [...emails].sort(
    (a, b) => displayName(b, profiles).length - displayName(a, profiles).length
  );
  let out = text;
  for (const email of byLongestName) {
    const name = displayName(email, profiles);
    if (name) out = out.split(`@${name}`).join(`@${email}`);
  }
  return out;
}

/**
 * Who this comment mentions, by name, for showing back to the writer.
 *
 * The text holds addresses, so while composing you see `@pat@example.com` and
 * have no confirmation you picked the right person — the name only appears once
 * the comment is posted. This is that confirmation, without changing what is
 * stored: the address is what makes a mention survive somebody being renamed.
 */
export function mentionedNames(text: string, profiles: Profiles): string[] {
  return mentionedIn(text).map((addr) => displayName(addr, profiles));
}
